import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import { Box, LinearProgress, SxProps } from '@bymason-private/legacy-pattern-components';
import XRayRemoteControl, {
  XrayRemoteControlConnectionInfo,
  XrayRemoteControlMethods,
  XrayRemoteControlProps,
} from '@bymason/xray-remote-control-viewer';
import { FormFactor } from '../types';
import { Theme } from '@bymason-private/controller-v1-theme-provider';

interface XrayViewerProps
  extends Omit<XrayRemoteControlProps, 'className' | 'clientToken' | 'viewportBorderRadius'> {
  formFactor: FormFactor;
  orientation: 'portrait' | 'landscape';
  autoRotateOrientation?: 'portrait' | 'landscape';
  start: boolean;
}

const deviceImageSx: Record<string, SxProps<Theme>> = {
  imageLandscapeD450: {
    transform: 'rotate(-90deg) translate(186px, 186px)',
  },
  imageLandscapeF210: {
    transform: 'rotate(-90deg) translate(136px, 134px)',
  },
  imageLandscapeG450: {
    transform: 'rotate(-90deg) translate(126px, 126px)',
  },
};

const canvasOrientationSx: Record<string, SxProps<Theme>> = {
  rcA4100: {
    height: '268px',
    left: '54px',
    top: '161px',
    width: '220px',
  },
  rcF210: {
    height: '499px',
    left: '19px',
    top: '44px',
    width: '300px',
  },
  rcF210Landscape: {
    height: '300px',
    left: '45px',
    top: '23px',
    width: '499px',
  },
  rcD450: {
    height: '607px',
    left: '12px',
    top: '51px',
    width: '314px',
  },
  rcD450Landscape: {
    height: '314px',
    left: '53px',
    top: '12px',
    width: '607px',
  },
  rcG450: {
    height: '467px',
    left: '23px',
    top: '60px',
    width: '293px',
  },
  rcG450Landscape: {
    height: '293px',
    left: '61px',
    top: '23px',
    width: '467px',
  },
  rcHide: {
    visibility: 'hidden',
  },
};

function RemoteControlViewer({
  formFactor,
  onConnectionInfo,
  orientation,
  // autoRotateOrientation,
  start,
  ...other
}: XrayViewerProps) {
  const { authToken = '' } = useAuth();
  const remoteControlRef = useRef<XrayRemoteControlMethods>(null);
  const [starting, setStarting] = useState(false);
  const [connectionEstablished, setConnectionEstablished] = useState(false);

  const handleConnectionInfo = useCallback(
    (info: XrayRemoteControlConnectionInfo) => {
      const isRunning = info.status === 'running';
      setConnectionEstablished(isRunning);
      setStarting(!isRunning);

      if (onConnectionInfo) {
        onConnectionInfo(info);
      }
    },
    [onConnectionInfo]
  );

  useEffect(() => {
    if (start) {
      remoteControlRef?.current?.start();
      setStarting(true);
    } else {
      remoteControlRef?.current?.stop();
      setStarting(false);
      setConnectionEstablished(false);
    }
  }, [start]);

  useEffect(() => {
    // give UI time enough to repaint with new
    // orientation aspect and then force an HTML
    // reflow, which will trigger the noVNC window
    // to refresh and repaint
    setTimeout(() => {
      void document.documentElement.offsetHeight;
      // trigger the resize listener for RFB to repaint the screen
      window.dispatchEvent(new Event('resize'));
    }, 500);
  }, [orientation]);

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'auto',
        flex: '0 1 340px',
        width: 340,
        position: 'relative',
      }}
    >
      {!connectionEstablished && (
        <Box
          sx={(theme) => ({
            alignItems: 'center',
            border: `1px solid ${theme.palette.grey[400]}`,
            borderRadius: 4,
            display: 'flex',
            flex: '0 1 472px',
            flexDirection: 'column',
            height: 600,
            justifyContent: 'center',
            position: 'relative',
            width: 472,
          })}
        >
          {starting && (
            <LinearProgress
              sx={{
                'MuiLinearProgress-root': {
                  left: 0,
                  position: 'absolute',
                  top: 0,
                  width: '100%',
                },
              }}
            />
          )}
          <Box
            component='img'
            width='260px'
            src='https://mcdn.bymason.com/runway/controller/xray-placeholder.png'
          />
        </Box>
      )}
      {connectionEstablished && (
        <Box
          component='img'
          sx={{
            height: '100%',
            width: '100%',
            transform: 'rotate(0deg)',
            transition: 'all .25s ease-out',
            transitionProperty: 'transform',
            ...(orientation === 'landscape' ? deviceImageSx[`imageLandscape${formFactor}`] || {} : {}),
          }}
          src={`https://mcdn.bymason.com/runway/controller/xray-${formFactor.toLocaleLowerCase()}.png`}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          transition: 'all .25s ease-out',
          transitionProperty: 'transform,height,left,top,width',
          ...(connectionEstablished
            ? canvasOrientationSx[`rc${formFactor}${orientation === 'landscape' ? 'Landscape' : ''}`] || {}
            : canvasOrientationSx.rcHide),
        }}
      >
        <XRayRemoteControl
          {...other}
          clientToken={authToken}
          onConnectionInfo={handleConnectionInfo}
          ref={remoteControlRef}
          viewportBorderRadius={formFactor === 'A4100' ? 34 : 0}
        />
      </Box>
    </Box>
  );
}

export default memo(RemoteControlViewer);
